import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import { When } from 'react-if'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { CaptionMD, CaptionSM, CaptionXS } from '@/atoms/Text'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { BoundedImage } from '@/molecules/BoundedImage'
import {
  ContentInteraction,
  DiscoverItemInteraction,
  HOVER_THRESHOLD_MS,
} from '@/services/Analytics/ContentInteractions'
import { LinkViewModel } from '@/services/RenderService'

type DiscoverBannerCardProps = {
  link: LinkViewModel
  itemIndex?: number
  createInteraction?: (link: LinkViewModel, itemIndex: number) => DiscoverItemInteraction | null
  trackInteraction?: (interaction: ContentInteraction, allowAutoImpression?: boolean) => void
}

export const DiscoverBannerCard: React.FC<DiscoverBannerCardProps> = ({
  link,
  itemIndex = 0,
  createInteraction,
  trackInteraction,
}) => {
  const [bannerRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9,
  })

  const hoverStartTimeRef = useRef<number | null>(null)
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)

  const backdrop = link.landscapeNonTitleImage
  const mobileBackdrop = link.squareNonTitleImage || link.landscapeNonTitleImage

  const releaseYear = link.releaseDate ? DateTime.fromISO(link.releaseDate).year : null

  const interaction = useMemo(() => createInteraction?.(link, itemIndex) ?? null, [createInteraction, link, itemIndex])

  const trackClick = useCallback(() => {
    if (interaction && trackInteraction) {
      const pressInteraction = interaction.withPress()
      trackInteraction(pressInteraction)
    }
  }, [interaction, trackInteraction])

  const handleMouseEnter = useCallback(() => {
    hoverStartTimeRef.current = Date.now()
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (!hoverStartTimeRef.current || !interaction || !trackInteraction) return

    const dwellTime = Date.now() - hoverStartTimeRef.current

    if (dwellTime >= HOVER_THRESHOLD_MS) {
      const hoverInteraction = interaction.withHover(dwellTime)
      trackInteraction(hoverInteraction)
    }

    hoverStartTimeRef.current = null
  }, [interaction, trackInteraction])

  // Track impression when visible for at least 1 second
  useEffect(() => {
    if (!interaction || !trackInteraction) return

    if (isVisible) {
      if (impressionTimeoutRef.current || hasTrackedImpression.current) {
        return
      }

      impressionTimeoutRef.current = setTimeout(() => {
        if (hasTrackedImpression.current) return
        hasTrackedImpression.current = true

        const impressionInteraction = interaction.withImpression()
        trackInteraction(impressionInteraction)

        impressionTimeoutRef.current = null
      }, 1000)
    } else {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [isVisible, interaction, trackInteraction])

  if (!backdrop) return null

  return (
    <InternalLink
      href={link.linkUrl}
      className="discover-banner-card !cursor-pointer"
      onClick={trackClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="px-[2px] md:px-4" ref={bannerRef}>
        <div
          className={classNames(
            'banner-content-container aspect-h-9 aspect-w-6 sm:aspect-w-3 sm:aspect-h-2 lg:aspect-h-1 lg:aspect-w-3 relative w-full overflow-hidden rounded-2xl border-2 border-core-gray-800 leading-[0] transition-all duration-500 hover:border-white',
          )}
        >
          {/* Desktop/tablet image */}
          <div className="hidden sm:block">
            <Image
              alt={link.name ?? ''}
              className="absolute inset-0 w-full rounded-xl"
              src={`/w_900,h_600,q_auto,c_thumb,g_north_east/${backdrop}.webp`}
              width={900}
              height={600}
            />
          </div>
          {/* Mobile image */}
          <div className="sm:hidden">
            <Image
              alt={link.name ?? ''}
              className="absolute inset-0 rounded-xl"
              src={`/w_600,h_900,q_auto,c_fill,g_north/${mobileBackdrop}.webp`}
              width={600}
              height={900}
            />
          </div>
          {/* Mobile gradient */}
          <div className="absolute bottom-0 mt-auto h-full w-full bg-[linear-gradient(to_bottom,_rgba(0,0,0,0)_0%,_rgba(0,0,0,0)_35%,_rgba(0,0,0,0.6)_60%,_black_85%,_black_100%)] md:hidden" />
          {/* Desktop gradient */}
          <div className="absolute bottom-0 mt-auto hidden h-full w-full bg-[linear-gradient(to_left,_rgba(0,0,0,0)_0%,_rgba(0,0,0,0)_35%,_rgba(0,0,0,0.6)_60%,rgba(0,0,0,0.8)_85%,_rgba(0,0,0,0.95)_100%)] md:block" />
          {/* Info section */}
          <div className="mx-auto flex h-full w-full flex-col items-start justify-end gap-2 px-4 pb-8 md:ml-[60px] md:w-[410px] md:justify-center md:px-0 md:pb-0 se:w-[300px] se:max-w-full">
            {link.logoUrl && (
              <BoundedImage
                alt={link.name ?? ''}
                className="mx-auto flex items-center justify-center md:mx-0 md:justify-start"
                imageClassName="max-h-[80px] sm:max-h-[150px] md:max-h-[120px] xl:max-h-[150px]"
                path={link.logoUrl}
                height={150}
                width={270}
              />
            )}
            <CaptionXS weight="medium" className="sm:photon-caption-md md:text-bold w-full text-center md:text-left">
              {link.description}
            </CaptionXS>
            <CaptionMD
              weight="medium"
              color="core-gray-400"
              className="mb-6 hidden w-full text-center sm:block md:text-left lg:mb-4 xl:mb-6"
            >
              {link.label}
            </CaptionMD>
            <div className="flex w-full flex-wrap items-center justify-center gap-[6px] md:justify-start">
              <When condition={Boolean(releaseYear)}>
                <CaptionSM
                  weight="medium"
                  className="md:photon-caption-md text-center md:text-left"
                  color="core-gray-500"
                >
                  {releaseYear}
                </CaptionSM>
                <When condition={(link.genres?.length ?? 0) > 0}>
                  <CaptionSM
                    weight="medium"
                    className="md:photon-caption-md text-center md:text-left"
                    color="core-gray-500"
                  >
                    •
                  </CaptionSM>
                </When>
              </When>
              {link.genres?.map((genre, index) => {
                if (!genre) return null
                return (
                  <CaptionSM
                    key={genre}
                    weight="medium"
                    className="md:photon-caption-md text-center md:text-left"
                    color="core-gray-500"
                  >
                    {genre}
                    {index < (link.genres?.length ?? 0) - 1 ? ',' : ''}
                  </CaptionSM>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </InternalLink>
  )
}
